// Setup › Structure › Job roles (`/c/{slug}/setup/roles`; SETUP-REGROUP renamed "Roles"; coverage matrix rows 14-16): the company's job roles
// (what a seat is for: "Recruiter", "Sourcer"), not access roles. List, "New role"
// (`POST …/roles`) and "Edit" (`PATCH …/roles/{id}`): name, description and the default metric
// a seat of this role starts with. One call on load, cursor-paged (plus the company's metrics
// while `kpi` is on). KPI-ASSIGN: the defaults are picked from a searchable list of the
// company's metrics and the DE catalog (name, unit, cadence; the key in small print), never
// typed as raw keys. The API checks every key against the company's definitions and the DE
// catalog and says which are unknown. ACTIONS: "Delete" (`DELETE …/roles/{id}`, managers) on a
// role no seat, skill policy or skill pack uses; the API's 409 says what still uses it.
import type { OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from "@companyos/ui";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { IdCardIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import { MetricMultiPicker, metricChoices } from "../kpi/metric-picker";
import { useAllMetrics } from "../kpi/queries";
import { metricTemplatesQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { useFeature } from "../use-feature";
import { problemText } from "./problem-text";
import { DENSE_TABLE, RowAction, RowActions } from "./row-actions";
import { SetupPage } from "./setup-page";

type Role = OperationOutput<"roles.list">["data"][number];

const rolesKey = (co: string) => ["company", co, "roles"] as const;
export const rolesQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: rolesKey(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.roles.list({
        params: { co },
        query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

const KEY = /^[a-z0-9][a-z0-9._-]*$/;

export function RolesScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useInfiniteQuery(rolesQuery(api, company.id));
  const kpi = useFeature("kpi");
  const metrics = useAllMetrics(api, company.id, kpi.enabled);
  const nameOf = new Map(metrics.all.map((m) => [m.key, m.name]));
  const [editing, setEditing] = useState<Role | "new" | null>(null);
  // ACTIONS: delete an unused role (the API refuses one a seat, policy or skill pack names).
  const qc = useQueryClient();
  const manager = isManager(company);
  const [deleting, setDeleting] = useState<Role | null>(null);
  const remove = useMutation({
    mutationFn: (r: Role) => api.client.roles.delete({ params: { co: company.id, id: r.id } }),
    onSuccess: (_d, r) => {
      setDeleting(null);
      toast.add({ title: `${r.name} deleted`, type: "success" });
    },
    onError: () => setDeleting(null),
    onSettled: () => qc.invalidateQueries({ queryKey: rolesKey(company.id) }),
  });
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <SetupPage
      title="Job roles"
      {...(q.data ? { count: rows.length } : {})}
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <PlusIcon data-icon="inline-start" />
          New role
        </Button>
      }
    >
      {q.isPending ? (
        <Loading label="Loading roles" rows={3} />
      ) : q.isError ? (
        <LoadError what="Job roles" error={q.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={IdCardIcon}
          title="No job roles yet"
          description="Like Recruiter or Sourcer."
          actions={
            <Button size="sm" onClick={() => setEditing("new")}>
              New role
            </Button>
          }
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <Table data-slot="setup-roles" className={DENSE_TABLE}>
            <TableCaption className="sr-only">Job roles of the company.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Role</TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Description
                </TableHead>
                <TableHead scope="col">Default metrics</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-role={r.id} className="group/row">
                  <TableHead scope="row" className="font-medium">
                    {r.name}
                  </TableHead>
                  <TableCell className="max-md:hidden text-muted-foreground">
                    {r.description ?? "—"}
                  </TableCell>
                  <TableCell>
                    <span className="flex flex-wrap gap-1">
                      {r.default_metric_keys.length
                        ? r.default_metric_keys.map((k) => (
                            <Badge
                              key={k}
                              variant="outline"
                              title={k}
                              className={nameOf.has(k) ? undefined : "font-mono"}
                            >
                              {nameOf.get(k) ?? k}
                            </Badge>
                          ))
                        : "—"}
                    </span>
                  </TableCell>
                  <TableCell className="w-px text-right">
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit ${r.name}`}
                        tip="Edit"
                        onClick={() => setEditing(r)}
                      />
                      {manager ? (
                        <RowAction
                          icon={Trash2Icon}
                          label={`Delete ${r.name}`}
                          tip="Delete"
                          className="hover:text-danger-text"
                          onClick={() => setDeleting(r)}
                        />
                      ) : null}
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <LoadMore
            what="roles"
            hasMore={q.hasNextPage}
            loading={q.isFetchingNextPage}
            onMore={() => void q.fetchNextPage()}
          />
        </div>
      )}
      {editing ? (
        <RoleDialog
          key={editing === "new" ? "new" : editing.id}
          api={api}
          company={company}
          role={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(o) => (o ? null : setDeleting(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Only a role no seat uses can be deleted. Move its seats to another role first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              Delete role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SetupPage>
  );
}

function RoleDialog({
  api,
  company,
  role,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  role: Role | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [list, setList] = useState<string[]>(role?.default_metric_keys ?? []);
  const [touched, setTouched] = useState(false);
  const badKey = list.find((k) => !KEY.test(k));
  // The choices: the company's metrics and the DE catalog (`kpi` routes, only while kpi is on).
  const kpi = useFeature("kpi");
  const metrics = useAllMetrics(api, company.id, kpi.enabled);
  const templates = useQuery({
    ...metricTemplatesQuery(api, company.id),
    enabled: kpi.enabled && isManager(company),
  });
  const choices = metricChoices(
    metrics.all.filter((m) => !m.archived_at),
    templates.data?.data ?? [],
  );
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        default_metric_keys: [...new Set(list)],
      };
      return role
        ? api.client.roles.update({ params: { co: company.id, id: role.id }, body })
        : api.client.roles.create({ params: { co: company.id }, body });
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: rolesKey(company.id) });
      toast.add({ title: role ? `${saved.name} saved` : `${saved.name} created`, type: "success" });
      onClose();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (name.trim() && !badKey) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>{role ? `Edit ${role.name}` : "New role"}</DialogTitle>
            <DialogDescription>
              Seats take a role; a new seat starts with the role's default metrics.
            </DialogDescription>
          </DialogHeader>
          <Field data-invalid={(touched && !name.trim()) || undefined}>
            <FieldLabel htmlFor="role-name">Name</FieldLabel>
            <Input
              id="role-name"
              className="h-10"
              value={name}
              aria-invalid={(touched && !name.trim()) || undefined}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="role-description">Description</FieldLabel>
            <Textarea
              id="role-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field data-invalid={badKey ? true : undefined}>
            <FieldLabel htmlFor="role-metrics">Default metrics</FieldLabel>
            <MetricMultiPicker
              id="role-metrics"
              value={list}
              choices={choices}
              onChange={setList}
              disabled={!kpi.enabled}
            />
            <FieldDescription id="role-metrics-help">
              {kpi.enabled
                ? "A new seat of this role starts with these KPIs (existing seats keep theirs)."
                : "Metrics need the KPI add-on."}
            </FieldDescription>
            {badKey ? (
              <p role="alert" className="text-xs text-danger-text">
                "{badKey}" is not a metric key: lowercase letters, digits, dots, dashes.
              </p>
            ) : null}
          </Field>
          {touched && !name.trim() ? (
            <p role="alert" className="text-xs text-danger-text">
              The role needs a name.
            </p>
          ) : null}
          {save.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "The role was not saved.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" />} disabled={save.isPending}>
              Cancel
            </DialogClose>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : role ? "Save" : "Create role"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
